import { put, select, call, all } from 'redux-saga/effects'
import { resetSection, SubmissionError } from 'redux-form'

import {
  limitSelector,
  systemSelector,
  placeSelector,
  phoneSelector
} from 'reduxModules/selectors/BookFormSelectors'
import { isPartnerSelector, isLimitedDispatcherSelector } from 'reduxModules/selectors/UserSelectors'
import { shownRoutesSelector, disabledCitiesSelector } from 'reduxModules/selectors/RouteSelectors'
import { savedBookingSelector } from 'reduxModules/selectors/BookSelectors'
import { sourcesSelector, mergedSourcesSelector } from 'reduxModules/selectors/SourceSelectors'
import { fromCityIdSelector, toCityIdSelector } from 'reduxModules/selectors/CitySelectors'
import { snackbarActions } from 'reduxModules/actions/SnackbarActions'
import { modalActions } from 'reduxModules/actions/ModalActions'
import { bookActions } from 'reduxModules/actions/BookActions'
import { routeActions } from 'reduxModules/actions/RouteActions'
import { passengerActions } from 'reduxModules/actions/PassengerActions'
import { getElIdFromPath } from 'services/helpers'
import {
  handleBooking,
  handleRouteInfo,
  createBooking,
  createSourceBooking,
  handleSourceData,
  addBookedPlace
} from 'services/sagaHelpers'

import { rsf } from '../../firebase'

export function* addBookingSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const place = yield select(placeSelector)
    const fromCityId = yield select(fromCityIdSelector)
    const toCityId = yield select(toCityIdSelector)
    const disabledCities = yield select(disabledCitiesSelector)
    const isPartner = yield select(isPartnerSelector)
    const isLimitedDispatcher = yield select(isLimitedDispatcherSelector)
    const limit = yield select(limitSelector)
    const system = yield select(systemSelector)
    const sources = yield select(sourcesSelector)
    const mergedSources = yield select(mergedSourcesSelector)
    const source = sources.find(({ id }) => system === id)
    const phone = yield select(phoneSelector)

    const bookings = system 
      ? yield createSourceBooking(source.label) 
      : yield createBooking()

    const overLimitBooking = bookings.length > limit

    const isFromCityDisabled = disabledCities.includes(fromCityId)
    const isFromToDisabled = disabledCities.includes(toCityId)

    /* eslint-disable */
    switch (true) {
      case overLimitBooking && (isPartner || system || isLimitedDispatcher):
        yield put(snackbarActions.showSnackbar({
          type: 'error',
          message: 'Недостатня к-ть вільних місць!'
        }))
        yield put(responseFailure())

        return
      case (isFromCityDisabled || isFromToDisabled) && (isPartner || isLimitedDispatcher):
        throw new SubmissionError({
          citiesBlocked: isFromCityDisabled || isFromToDisabled
        })
      case isPartner:
        yield put(bookActions.saveBooking(bookings))
        yield put(modalActions.showModal({ type: 'paidStatus' }))

        return
      case overLimitBooking:
        yield put(bookActions.saveBooking(bookings))

        yield put(modalActions.showModal({ type: 'overLimit' }))

        return
    }
    /* eslint-enable */

    const res = yield handleBooking(bookings)

    if (res[0]) {
      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Бронювання успішно добавленно!'
      }))

      if (system) {
        const value = (source.booked || 0) + bookings.length

        const updatedSources = mergedSources.map(source => {
          const { id, booked } = source

          if (id === system) {
            return {
              ...source,
              booked: (booked || 0) + value
            }
          } else {
            return source
          }
        })

        yield put(routeActions.changeSourceData(updatedSources))
        yield handleSourceData()
      } else {
        yield handleRouteInfo(bookings)
      }

      yield addBookedPlace(place)

      yield put(
        resetSection(
          'book',
          'from',
          'to',
          'addPax',
          'system',
          'note',
          'surname',
          'name',
          'phone',
          'price',
          'place'
        )
      )
      yield put(responseSuccess())

      yield put(passengerActions.setPassenger({
        phone,
        firstName: bookings[0].name,
        lastName: bookings[0].surname,
      }))

      yield all(
        res.map(({ id }) => put(passengerActions.setPassengerBooking({ bookingId: id, phone })))
      )
    } else {
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Сталась помилка під час бронювання, будь ласка повторіть спробу'
      }))
      yield put(responseFailure())
    }
  } catch (err) {
    console.log(err)
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час бронювання, будь ласка перевірте введені дані і повторіть спробу'
    }))
    yield put(responseFailure(err))
  }
}

export function* addSavedBookingSaga(action) {
  const { responseSuccess, responseFailure, data } = action

  try {
    const place = yield select(placeSelector)
    const savedBooking = yield select(savedBookingSelector)
    const phone = yield select(phoneSelector)

    const res = yield handleBooking(savedBooking, data)

    if (res[0]) {
      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Бронювання успішно добавленно!'
      }))

      yield handleRouteInfo(savedBooking)

      yield addBookedPlace(place)

      yield put(
        resetSection(
          'book',
          'from',
          'to',
          'addPax',
          'system',
          'note',
          'surname',
          'name',
          'phone',
          'price',
          'place'
        )
      )

      yield put(passengerActions.setPassenger({
        phone,
        firstName: savedBooking[0].name,
        lastName: savedBooking[0].surname,
      }))

      yield all(
        res.map(({ id }) => put(passengerActions.setPassengerBooking({ bookingId: id, phone })))
      )

      yield put(responseSuccess())
    } else {
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Сталась помилка під час бронювання, будь ласка повторіть спробу'
      }))
      yield put(responseFailure())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure(err))
  }
}

export function* getDepartureTimeSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const routeIds = yield select(shownRoutesSelector)
    const fromCityId = yield select(fromCityIdSelector)
    
    const res = yield all(
      routeIds.map(routeId => call(rsf.firestore.getDocument, `routes/${routeId}/cities/${fromCityId}`))
    )

    const departureTime = res.reduce((acc, item) => {
      const routeId = getElIdFromPath(item.ref.path, 2)

      acc[routeId] = item.data().departureTime

      return acc
    }, {})

    yield put(responseSuccess(departureTime))
  } catch (err) {
    console.log(err)
    yield put(responseFailure(err))
  }
}



// WEBPACK FOOTER //
// ./src/reduxModules/sagas/BookSagas.js